import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../ui/Button";
import { formatValue } from "../../../widgets/format";
import { counterSpan } from "../../../widgets/station";
import { counterPeriodsQuery } from "./queries";

/** A reset counter's Earlier list (weather-station-widgets WSW-14): the last 20 periods, newest first, with who reset. */
export function CounterPeriodsDialog({
  orgId,
  device,
  datapoint,
  title,
  decimals,
  now,
  onClose,
}: {
  orgId: string;
  device: string;
  datapoint: string;
  title: string;
  decimals: number;
  now: Date;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { t: tw, i18n } = useTranslation("widget");
  const dialog = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const periods = useQuery(counterPeriodsQuery(orgId, device, datapoint));
  useEffect(() => {
    const d = dialog.current;
    if (d && !d.open) d.showModal();
  }, []);
  const items = periods.data?.items ?? [];
  return (
    <dialog
      ref={dialog}
      onClose={onClose}
      aria-labelledby={titleId}
      className="m-auto w-full max-w-md rounded-lg p-0 shadow-xl backdrop:bg-black/40"
    >
      <div className="space-y-4 p-6">
        <h2 id={titleId} className="text-lg font-semibold text-gray-900">
          {tw("counter.earlier")}: {title}
        </h2>
        {periods.isPending ? null : items.length === 0 ? (
          <p className="text-sm text-gray-700">{tw("state.noData")}</p>
        ) : (
          <ul className="max-h-80 divide-y divide-gray-100 overflow-auto">
            {items.map((p) => (
              <li key={p.to} className="flex items-baseline justify-between gap-3 py-2 text-sm">
                <span className="min-w-0">
                  <span className="block text-gray-900">{counterSpan(p.from, p.to, now, tw)}</span>
                  {p.reset_by_name && (
                    <span className="block text-xs text-gray-700">
                      {tw("counter.resetBy", { name: p.reset_by_name })}
                    </span>
                  )}
                </span>
                <span className="font-semibold text-gray-900 tabular-nums">
                  {p.value === null ? "—" : formatValue(p.value, periods.data?.unit ?? null, decimals, i18n.language)}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => dialog.current?.close()}>
            {t("common.close")}
          </Button>
        </div>
      </div>
    </dialog>
  );
}
